<template>
  <ds-dialog
    id="warning-banner-form"
    :title="`${isEdit ? 'Wijzig' : 'Nieuwe'} waarschuwingsbanner`"
    size="md"
    as="form"
    @close="$emit('cancel')"
    @submit.prevent="onSubmit"
  >
    <div class="ds-form">
      <div class="ds-form__field">
        <label>Merken *</label>
        <div class="ds-segmented" role="radiogroup" aria-label="Merken">
          <button
            type="button"
            class="ds-segmented__option"
            :class="{ 'ds-segmented__option--active': form.AllBrands }"
            role="radio"
            :aria-checked="form.AllBrands"
            @click="form.AllBrands = true"
          >
            Alle merken
          </button>
          <button
            type="button"
            class="ds-segmented__option"
            :class="{ 'ds-segmented__option--active': !form.AllBrands }"
            role="radio"
            :aria-checked="!form.AllBrands"
            @click="form.AllBrands = false"
          >
            Selectie van merken
          </button>
        </div>
        <template v-if="!form.AllBrands">
          <div class="warning-banner-form__brands">
            <label
              v-for="brand in brandOptions"
              :key="brand.value"
              class="warning-banner-form__brand"
            >
              <input
                v-model="form.BrandCodes"
                type="checkbox"
                :value="brand.value"
                :disabled="brand.value === ownBrand"
              />
              {{ brand.label }}
            </label>
          </div>
          <span class="ds-form__hint">
            {{ ownBrandName }} is altijd geselecteerd: je beheert de banner vanuit dit merk.
          </span>
        </template>
      </div>

      <div class="ds-form__row">
        <div class="ds-form__field">
          <label for="backgroundColor">Kleur *</label>
          <select id="backgroundColor" v-model="form.BackgroundColor" required class="ds-field">
            <option value="red">Rood</option>
            <option value="dimgray">Licht grijs</option>
            <option value="orange">Oranje</option>
          </select>
        </div>
      </div>

      <div class="ds-form__field">
        <label for="textBlock">Tekst (HTML toegestaan) *</label>
        <textarea
          id="textBlock"
          v-model="form.TextBlock"
          rows="4"
          required
          class="ds-field"
          placeholder='Bijv. Gepland onderhoud op zondag. &lt;a href="#"&gt;Meer info&lt;/a&gt;'
        ></textarea>
      </div>

      <div v-if="form.TextBlock">
        <div class="warning-banner-form__preview-label">Voorbeeld</div>
        <div
          class="warning-banner-form__preview"
          :style="{ backgroundColor: form.BackgroundColor }"
          v-html="form.TextBlock"
        ></div>
      </div>

      <div class="ds-form__row">
        <div class="ds-form__field">
          <label for="dateFrom">Begindatum *</label>
          <input
            id="dateFrom"
            v-model="form.DateFrom"
            type="datetime-local"
            required
            class="ds-field"
          />
        </div>

        <div class="ds-form__field">
          <label for="dateTo">Einddatum</label>
          <input id="dateTo" v-model="form.DateTo" type="datetime-local" class="ds-field" />
          <span class="ds-form__hint">Leeg laten als er geen einddatum is.</span>
        </div>
      </div>

      <div v-if="error" class="ds-alert ds-alert--danger">{{ error }}</div>
    </div>

    <template #footer>
      <ds-button
        variant="text"
        type="button"
        data-qa="warning-banner-form-cancel"
        @click="$emit('cancel')"
      >
        Annuleren
      </ds-button>
      <ds-button type="submit" data-qa="warning-banner-form-submit" :disabled="loading">
        <ds-spinner v-if="loading" inline />
        <ds-icon v-else-if="isEdit" name="edit" />
        <ds-icon v-else name="save" />
        {{ loading ? 'Opslaan…' : isEdit ? 'Wijzig' : 'Opslaan' }}
      </ds-button>
    </template>
  </ds-dialog>
</template>

<script>
import warningBannerService from '@/services/warningBannerService'
import moment from 'moment'
import { mapState } from 'vuex'
import DsDialog from '@/components/ds/molecules/dsDialog/DsDialog.vue'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import DsIcon from '@/components/ds/atoms/dsIcon/DsIcon.vue'
import DsSpinner from '@/components/ds/atoms/dsSpinner/DsSpinner.vue'

const BRAND_MAP = {
  '*': 'Alle merken',
  V: 'Volkswagen',
  N: 'Volkswagen bedrijfswagens',
  S: 'Seat',
  C: 'Skoda',
  A: 'Audi',
}

export default {
  name: 'WarningBannerForm',
  components: { DsDialog, DsButton, DsIcon, DsSpinner },
  props: {
    initial: {
      type: Object,
      default: null,
    },
  },
  emits: ['cancel', 'saved'],
  data() {
    return {
      form: {
        Id: null,
        AllBrands: true,
        BrandCodes: [],
        BackgroundColor: 'red',
        TextBlock: '',
        DateFrom: '',
        DateTo: '',
      },
      loading: false,
      error: null,
    }
  },
  computed: {
    ...mapState('user', ['dealerSettings']),
    ownBrand() {
      return this.dealerSettings.brand
    },
    ownBrandName() {
      return BRAND_MAP[this.ownBrand] || this.ownBrand
    },
    brandOptions() {
      return Object.entries(BRAND_MAP)
        .filter(([value]) => value !== '*')
        .map(([value, label]) => ({ value, label }))
    },
    isEdit() {
      return this.initial && this.initial.Id
    },
  },
  watch: {
    initial: {
      handler() {
        this.initializeForm()
      },
      immediate: true,
    },
  },
  mounted() {
    this.initializeForm()
  },
  methods: {
    initializeForm() {
      if (this.initial) {
        const codes = this.initial.BrandCodes || ['*']
        const allBrands = codes.includes('*')
        this.form = {
          Id: this.initial.Id,
          AllBrands: allBrands,
          BrandCodes: allBrands ? [this.ownBrand] : [...new Set([this.ownBrand, ...codes])],
          BackgroundColor: this.initial.BackgroundColor || 'red',
          TextBlock: this.initial.TextBlock || '',
          DateFrom: this.initial.DateFrom ? this.initial.DateFrom.slice(0, 16) : '',
          DateTo: this.initial.DateTo ? this.initial.DateTo.slice(0, 16) : '',
        }
      } else {
        this.form.BrandCodes = [this.ownBrand]
        // Set default start time to now, local date time
        const localDateTime = moment().format('YYYY-MM-DDTHH:mm:ss.SSS')
        this.form.DateFrom = localDateTime.slice(0, 16)
      }
    },
    async onSubmit() {
      this.error = null
      this.loading = true

      try {
        // Convert local datetime to ISO string for API
        const warningBanner = {
          BrandCodes: this.form.AllBrands ? ['*'] : this.form.BrandCodes,
          BackgroundColor: this.form.BackgroundColor,
          TextBlock: this.form.TextBlock.trim(),
          DateFrom: this.form.DateFrom,
          DateTo: this.form.DateTo,
        }

        // Add ID for updates
        if (this.isEdit) {
          warningBanner.Id = this.form.Id
        }
        const apiCall = this.isEdit
          ? warningBannerService.updateWarningBanner(this.form.Id, warningBanner)
          : warningBannerService.createNewWarningBanner(warningBanner)

        await apiCall
        this.$emit('saved')
      } catch (error) {
        console.error('Save error:', error)
        this.error =
          error.response?.data?.message ||
          error.message ||
          'Opslaan van de banner is mislukt. Probeer het opnieuw.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Layout only — the dialog, form grid, fields, buttons and error banner all
// come from the design system (styles/ds/).

.warning-banner-form__preview-label {
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ds-color-text-subtle);
}

// The banner colours are admin-chosen and always dark, so the preview text is
// white with a shadow to stay legible on any of them — same as the live banner.
.warning-banner-form__preview {
  margin-top: 4px;
  padding: 14px var(--ds-space-4);
  border-radius: var(--ds-radius-md);
  color: #fff;
  font-size: var(--ds-font-size-sm);
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);

  :deep(a) {
    color: #fff;
    text-decoration: underline;
  }
}

.warning-banner-form__brands {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ds-space-2) var(--ds-space-4);
}

// .ds-form__field styles every label as an uppercase field caption; these are option labels.
.ds-form__field .warning-banner-form__brand {
  gap: 6px;
  font-size: var(--ds-font-size-sm);
  font-weight: 400;
  text-transform: none;
  letter-spacing: normal;
  color: var(--ds-color-text);
  cursor: pointer;
}
</style>
